import React, { useState } from 'react';
import ConfirmationModal from './ConfirmationModal';
import { QuizMode, CustomQuizConfig, FlashcardConfig, Question, User } from '../types';
import CustomQuizModal from './CustomQuizModal';
import FlashcardConfigModal from './FlashcardConfigModal';
import ChangePasswordModal from './ChangePasswordModal';

interface StartScreenProps {
    onStart: (options: { resume: boolean; mode?: QuizMode, customConfig?: CustomQuizConfig }) => void;
    onStartFlashcards: (config: FlashcardConfig) => void;
    hasSavedProgress: boolean;
    onShowHistory: () => void;
    hasHistory: boolean;
    allQuestions: Question[];
    currentUser: User | null; // Added currentUser prop
    onShowAdminPanel: () => void;
    subtitle: string;
    displayLanguage: 'pt' | 'en' | 'es';
}

const StartScreen: React.FC<StartScreenProps> = ({ onStart, onStartFlashcards, hasSavedProgress, onShowHistory, hasHistory, allQuestions, currentUser, onShowAdminPanel, subtitle, displayLanguage }) => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedMode, setSelectedMode] = useState<QuizMode | null>(null);
    const [isCustomModalOpen, setIsCustomModalOpen] = useState(false);
    const [isFlashcardModalOpen, setIsFlashcardModalOpen] = useState(false);
    const [showChangePassword, setShowChangePassword] = useState(false);

    const handleStartClick = (mode: QuizMode) => {
        if (hasSavedProgress) {
            setSelectedMode(mode);
            setIsModalOpen(true);
        } else {
            onStart({ resume: false, mode });
        }
    };

    const confirmStartNew = () => {
        if (selectedMode) {
            onStart({ resume: false, mode: selectedMode });
        }
        setIsModalOpen(false);
        setSelectedMode(null);
    };

    const cancelStartNew = () => {
        setIsModalOpen(false);
        setSelectedMode(null);
    };

    const handleStartCustomQuiz = (config: CustomQuizConfig) => {
        setIsCustomModalOpen(false);
        onStart({ resume: false, mode: 'practice', customConfig: config });
    };

    const handleStartFlashcards = (config: FlashcardConfig) => {
        setIsFlashcardModalOpen(false);
        onStartFlashcards(config);
    };


    return (
        <>
            <ConfirmationModal
                isOpen={isModalOpen}
                onConfirm={confirmStartNew}
                onCancel={cancelStartNew}
                title={displayLanguage === 'en' ? 'Start New Exam?' : displayLanguage === 'es' ? '¿Iniciar nuevo examen?' : 'Iniciar Novo Exame?'}
            >
                {displayLanguage === 'en'
                    ? 'You have a saved exam in progress. Do you want to discard your progress and start a new one?'
                    : displayLanguage === 'es'
                        ? 'Tienes un examen guardado en curso. ¿Quieres descartar tu progreso e iniciar uno nuevo?'
                        : 'Você tem um exame em andamento salvo. Deseja descartar seu progresso e iniciar um novo exame?'}
            </ConfirmationModal>

            <CustomQuizModal
                isOpen={isCustomModalOpen}
                onStart={handleStartCustomQuiz}
                onCancel={() => setIsCustomModalOpen(false)}
                allQuestions={allQuestions}
                displayLanguage={displayLanguage}
            />

            <FlashcardConfigModal
                isOpen={isFlashcardModalOpen}
                onStart={handleStartFlashcards}
                onCancel={() => setIsFlashcardModalOpen(false)}
                hasHistory={hasHistory}
                allQuestions={allQuestions}
                displayLanguage={displayLanguage}
            />

            <div className="bg-white dark:bg-slate-800 p-8 rounded-lg shadow-2xl text-center flex flex-col items-center animate-fade-in">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-8">
                    {displayLanguage === 'en' ? 'Available Study Modes' : displayLanguage === 'es' ? 'Modos de estudio disponibles' : subtitle}
                </h2>
                
                <div className="w-full grid grid-cols-1 md:grid-cols-2 gap-6 text-left mb-8">
                    
                    {/* Modo Flashcard */}
                    <div className="bg-gray-50 dark:bg-slate-700/50 p-6 rounded-lg flex flex-col">
                        <h3 className="text-xl font-bold text-teal-400 mb-3"><i className="far fa-clone mr-2"></i>{displayLanguage === 'en' ? 'Study Mode' : displayLanguage === 'es' ? 'Modo de estudio' : 'Modo de Estudo'}</h3>
                        <ul className="list-disc list-inside space-y-2 text-gray-600 dark:text-slate-300 flex-grow">
                           <li>{displayLanguage === 'en' ? 'Quick review with flashcards.' : displayLanguage === 'es' ? 'Revisión rápida con flashcards.' : 'Revisão rápida com flashcards.'}</li>
                           <li>{displayLanguage === 'en' ? 'Filter by domain or incorrect questions.' : displayLanguage === 'es' ? 'Filtra por dominio o preguntas incorrectas.' : 'Filtre por domínio ou questões erradas.'}</li>
                           <li>{displayLanguage === 'en' ? 'Ideal for memorizing key concepts.' : displayLanguage === 'es' ? 'Ideal para memorizar conceptos clave.' : 'Ideal para memorizar conceitos chave.'}</li>
                        </ul>
                        <div className="mt-6">
                            <button
                                onClick={() => setIsFlashcardModalOpen(true)}
                                className="bg-teal-600 hover:bg-teal-700 text-white font-bold py-3 px-6 rounded-lg transition-transform duration-200 transform hover:scale-105 shadow-lg w-full"
                            >
                                {displayLanguage === 'en' ? 'Start Study' : displayLanguage === 'es' ? 'Iniciar estudio' : 'Iniciar Estudo'}
                            </button>
                        </div>
                    </div>

                     {/* Modo de Prática */}
                     <div className="bg-gray-50 dark:bg-slate-700/50 p-6 rounded-lg flex flex-col">
                        <h3 className="text-xl font-bold text-orange-400 mb-3"><i className="fas fa-pencil-alt mr-2"></i>{displayLanguage === 'en' ? 'Practice Mode' : displayLanguage === 'es' ? 'Modo de práctica' : 'Modo de Prática'}</h3>
                        <ul className="list-disc list-inside space-y-2 text-gray-600 dark:text-slate-300 flex-grow">
                           <li>{displayLanguage === 'en' ? 'Get instant feedback for each question.' : displayLanguage === 'es' ? 'Recibe feedback inmediato para cada pregunta.' : 'Receba feedback instantâneo para cada questão.'}</li>
                           <li>{displayLanguage === 'en' ? 'Check your answer and see the explanation right away.' : displayLanguage === 'es' ? 'Verifica tu respuesta y mira la explicación al instante.' : 'Verifique sua resposta e veja a explicação imediatamente.'}</li>
                           <li>{displayLanguage === 'en' ? 'Ideal for learning and reinforcing concepts.' : displayLanguage === 'es' ? 'Ideal para aprender y reforzar conceptos.' : 'Ideal para aprender e reforçar conceitos.'}</li>
                        </ul>
                         <div className="mt-6">
                            <button
                                onClick={() => handleStartClick('practice')}
                                className="bg-orange-500 hover:bg-orange-600 text-white font-bold py-3 px-6 rounded-lg transition-transform duration-200 transform hover:scale-105 shadow-lg w-full"
                            >
                                {hasSavedProgress
                                    ? (displayLanguage === 'en' ? 'New Exam (Practice)' : displayLanguage === 'es' ? 'Nuevo examen (práctica)' : 'Novo Exame (Prática)')
                                    : (displayLanguage === 'en' ? 'Start Practice' : displayLanguage === 'es' ? 'Iniciar práctica' : 'Iniciar Prática')}
                            </button>
                        </div>
                    </div>

                    {/* Simulado Personalizado */}
                    <div className="bg-gray-50 dark:bg-slate-700/50 p-6 rounded-lg flex flex-col">
                        <h3 className="text-xl font-bold text-purple-400 mb-3"><i className="fas fa-tasks mr-2"></i>{displayLanguage === 'en' ? 'Custom Exam' : displayLanguage === 'es' ? 'Examen personalizado' : 'Simulado Personalizado'}</h3>
                        <ul className="list-disc list-inside space-y-2 text-gray-600 dark:text-slate-300 flex-grow">
                           <li>{displayLanguage === 'en' ? 'Focus on specific domains.' : displayLanguage === 'es' ? 'Enfócate en dominios específicos.' : 'Foque em domínios específicos.'}</li>
                           <li>{displayLanguage === 'en' ? 'Choose the number of questions.' : displayLanguage === 'es' ? 'Elige la cantidad de preguntas.' : 'Escolha o número de questões.'}</li>
                           <li>{displayLanguage === 'en' ? 'Perfect to review weak points.' : displayLanguage === 'es' ? 'Perfecto para revisar tus puntos débiles.' : 'Perfeito para revisar seus pontos fracos.'}</li>
                        </ul>
                         <div className="mt-6">
                            <button
                                onClick={() => setIsCustomModalOpen(true)}
                                className="bg-purple-600 hover:bg-purple-700 text-white font-bold py-3 px-6 rounded-lg transition-transform duration-200 transform hover:scale-105 shadow-lg w-full"
                            >
                                {displayLanguage === 'en' ? 'Create Exam' : displayLanguage === 'es' ? 'Crear examen' : 'Criar Simulado'}
                            </button>
                        </div>
                    </div>

                     {/* Modo de Simulado */}
                     <div className="bg-gray-50 dark:bg-slate-700/50 p-6 rounded-lg flex flex-col">
                        <h3 className="text-xl font-bold text-cyan-400 mb-3"><i className="fas fa-stopwatch mr-2"></i>{displayLanguage === 'en' ? 'Exam Mode' : displayLanguage === 'es' ? 'Modo de examen' : 'Modo de Simulado'}</h3>
                         <ul className="list-disc list-inside space-y-2 text-gray-600 dark:text-slate-300 flex-grow">
                           <li>{displayLanguage === 'en' ? 'Replicates the real exam experience.' : displayLanguage === 'es' ? 'Replica la experiencia real del examen.' : 'Replica a experiência real do exame.'}</li>
                           <li>{displayLanguage === 'en' ? 'Answer all questions without feedback.' : displayLanguage === 'es' ? 'Responde todas las preguntas sin feedback.' : 'Responda a todas as questões sem feedback.'}</li>
                           <li>{displayLanguage === 'en' ? 'Results are shown only at the end.' : displayLanguage === 'es' ? 'Los resultados se muestran solo al final.' : 'Os resultados são revelados apenas no final.'}</li>
                        </ul>
                        <div className="mt-6">
                            <button
                                onClick={() => handleStartClick('exam')}
                                className="bg-cyan-600 hover:bg-cyan-700 text-white font-bold py-3 px-6 rounded-lg transition-transform duration-200 transform hover:scale-105 shadow-lg w-full"
                            >
                                {hasSavedProgress
                                    ? (displayLanguage === 'en' ? 'New Exam (Timed)' : displayLanguage === 'es' ? 'Nuevo examen (temporizado)' : 'Novo Exame (Simulado)')
                                    : (displayLanguage === 'en' ? 'Start Exam' : displayLanguage === 'es' ? 'Iniciar examen' : 'Iniciar Simulado')}
                            </button>
                        </div>
                    </div>
                </div>

                {/* Removido: seleção de simulado pelo usuário. Agora controlado via painel admin. */}

                {/* Botões de continuação (se aplicável) */}
                {(hasSavedProgress || hasHistory) && (
                    <div className="w-full max-w-xl mx-auto mt-6 border-t border-gray-200 dark:border-slate-700 pt-6">
                        <div className={`grid grid-cols-1 ${(hasSavedProgress && hasHistory) ? 'sm:grid-cols-2' : ''} gap-4`}>
                            {hasSavedProgress && (
                                <button
                                    onClick={() => onStart({ resume: true })}
                                    className="bg-green-600 hover:bg-green-700 text-white font-bold py-3 px-8 rounded-lg transition-transform duration-200 transform hover:scale-105 shadow-lg"
                                >
                                    <i className="fas fa-play mr-2"></i>
                                    {displayLanguage === 'en' ? 'Resume Saved Exam' : displayLanguage === 'es' ? 'Reanudar examen guardado' : 'Retomar Exame Salvo'}
                                </button>
                            )}
                            {hasHistory && (
                                <button
                                    onClick={onShowHistory}
                                    className="bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-3 px-8 rounded-lg transition-transform duration-200 transform hover:scale-105 shadow-lg"
                                >
                                    <i className="fas fa-chart-line mr-2"></i>
                                    {displayLanguage === 'en' ? 'View Progress' : displayLanguage === 'es' ? 'Ver progreso' : 'Ver Progresso'}
                                </button>
                            )}
                        </div>
                    </div>
                )}

                {/* Admin Panel Button */}
                {currentUser?.role === 'admin' && (
                    <div className="w-full max-w-xl mx-auto mt-6 border-t border-gray-200 dark:border-slate-700 pt-6">
                       <button
                            onClick={onShowAdminPanel}
                            className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-3 px-8 rounded-lg transition-transform duration-200 transform hover:scale-105 shadow-lg"
                        >
                            <i className="fas fa-user-shield mr-2"></i> {displayLanguage === 'en' ? 'Admin Panel' : displayLanguage === 'es' ? 'Panel de administración' : 'Painel de Administração'}
                        </button>
                    </div>
                )}

                
            </div>
            {currentUser && (
                <ChangePasswordModal isOpen={showChangePassword} onClose={() => setShowChangePassword(false)} currentUser={currentUser} />
            )}
        </>
    );
};

export default StartScreen;
